import { useI18nStore, useT } from '@/i18n';
import { formatRelative } from '@/utils/datetime';
import { useAui, useAuiState } from '@assistant-ui/react';
import {
  Block,
  BlockTitle,
  Link as F7Link,
  List,
  ListInput,
  ListItem,
  Preloader,
  SwipeoutActions,
  SwipeoutButton,
  Toolbar,
  f7,
} from 'framework7-react';
import type { FocusEvent } from 'react';

interface Props {
  open: boolean;
  renamingId: string | null;
  onRenamingChange: (threadId: string | null) => void;
  onClose: () => void;
}

export function ThreadListPanel({ open, renamingId, onRenamingChange, onClose }: Props) {
  const t = useT().aiAssistant;
  const locale = useI18nStore((s) => s.locale);
  const aui = useAui();
  const items = useAuiState((s) => s.threads.threadItems);
  const isLoading = useAuiState((s) => s.threads.isLoading);
  const loadFailed = useAuiState(
    (s) => s.threads.loadError !== undefined && s.threads.loadError !== null,
  );

  const withRemote = items.filter((item) => item.remoteId !== undefined);
  const active = withRemote.filter((item) => item.status === 'regular');
  const archived = withRemote.filter((item) => item.status === 'archived');

  const run = async (action: () => void | Promise<void>) => {
    try {
      await action();
    } catch {
      f7.toast.show({ text: t.threadActionFailed, closeTimeout: 3000, position: 'bottom' });
    }
  };

  const pick = (id: string) => {
    aui.threads.switchToThread(id, { unarchive: false });
    onClose();
  };

  const commitRename = (id: string, value: string) => {
    const title = value.trim();
    onRenamingChange(null);
    if (title) void run(() => aui.threads.item({ id }).rename(title));
  };

  const confirmDelete = (id: string) => {
    f7.actions
      .create({
        buttons: [
          [
            { text: t.deleteTitle, label: true },
            {
              text: t.delete,
              color: 'red',
              onClick: () => void run(() => aui.threads.item({ id }).delete()),
            },
            { text: t.cancel },
          ],
        ],
      })
      .open();
  };

  const renderRows = (rows: typeof withRemote, isArchived: boolean) =>
    rows.map((item) =>
      renamingId === item.id ? (
        <ListInput
          key={item.id}
          type="text"
          defaultValue={item.title ?? ''}
          autofocus
          inputId={`ai-thread-rename-${item.id}`}
          data-testid={`ai-thread-rename-input-${item.remoteId}`}
          onBlur={(event: FocusEvent<HTMLInputElement>) =>
            commitRename(item.id, event.target.value)
          }
        />
      ) : (
        <ListItem
          key={item.id}
          link
          swipeout
          className="ai-thread-item"
          title={item.title ?? t.untitled}
          after={item.lastMessageAt ? formatRelative(item.lastMessageAt.toISOString(), locale) : ''}
          data-testid={`ai-thread-${item.remoteId}`}
          onClick={() => pick(item.id)}
        >
          <SwipeoutActions right>
            <SwipeoutButton
              data-testid={`ai-thread-rename-${item.remoteId}`}
              onClick={() => onRenamingChange(item.id)}
            >
              {t.rename}
            </SwipeoutButton>
            <SwipeoutButton
              color="orange"
              data-testid={`ai-thread-archive-${item.remoteId}`}
              onClick={() =>
                void run(() =>
                  isArchived
                    ? aui.threads.item({ id: item.id }).unarchive()
                    : aui.threads.item({ id: item.id }).archive(),
                )
              }
            >
              {isArchived ? t.unarchive : t.archive}
            </SwipeoutButton>
            <SwipeoutButton
              color="red"
              data-testid={`ai-thread-delete-${item.remoteId}`}
              onClick={() => confirmDelete(item.id)}
            >
              {t.delete}
            </SwipeoutButton>
          </SwipeoutActions>
        </ListItem>
      ),
    );

  return (
    <div className={`ai-thread-panel${open ? ' ai-thread-panel-open' : ''}`} aria-hidden={!open}>
      <Toolbar>
        <div className="left">
          <F7Link href={false} iconF7="xmark" sheetClose data-testid="ai-threads-close-sheet" />
        </div>
        <div className="title">{t.conversations}</div>
        <div className="right">
          <F7Link
            href={false}
            iconF7="chevron_right"
            data-testid="ai-threads-back"
            onClick={onClose}
          />
        </div>
      </Toolbar>
      <div className="ai-thread-panel-content">
        {isLoading && withRemote.length === 0 && (
          <Block className="text-align-center">
            <Preloader />
          </Block>
        )}
        {loadFailed && (
          <Block className="ai-thread-panel-empty">
            <div>{t.loadThreadsError}</div>
            <F7Link
              href={false}
              data-testid="ai-threads-retry"
              onClick={() => void aui.threads.reload()}
            >
              {t.retry}
            </F7Link>
          </Block>
        )}
        {!isLoading && !loadFailed && withRemote.length === 0 && (
          <Block className="ai-thread-panel-empty">{t.noConversations}</Block>
        )}
        {active.length > 0 && (
          <List strongIos insetIos dividersIos mediaList className="ai-thread-list">
            {renderRows(active, false)}
          </List>
        )}
        {archived.length > 0 && (
          <>
            <BlockTitle>{t.archived}</BlockTitle>
            <List strongIos insetIos dividersIos mediaList className="ai-thread-list">
              {renderRows(archived, true)}
            </List>
          </>
        )}
      </div>
    </div>
  );
}
